import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

import Box from 'shopper/components/Box';

import OfferSearchListCard from 'components/Timeline/OfferCard/V1/OfferSearchListCard';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import { isOfferFinished, isValidApprovedOffer } from 'lib/offer';
import { userQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import { getUserOffers } from 'services/user';

import EmptyMyOffersList from './EmptyMyOffersList';
import Loading from './Loading';

import OFFER from 'constants/offer';

const ListLoadMoreButton = dynamic(
  () => import('components/ListLoadMoreButton'),
  {
    ssr: false,
  }
);

const MyOffersList = ({ gaEventCategory, isAuthLoading }) => {
  const fetchNextPageRef = useRef(null);
  const { LoggedInUser, isLoggedIn, loadingLoggedInUser } = useUser();
  const [isScrollPaginationEnabled, setScrollPaginationEnabled] =
    useState(false);

  const {
    data: offers,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isInitialLoading,
    isLoading,
    isSuccess,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: userQueryKeys.interactionsList({
      userId: LoggedInUser?.userId,
      activeTabId: 'offers',
    }),
    queryFn: async (after, { signal }) => {
      if (after) {
        setScrollPaginationEnabled(true);
      }

      return getUserOffers(
        {
          userUsername: LoggedInUser.userUsername,
          after,
          limit: OFFER.MY_OFFERS_PAGINATION_LIMIT,
        },
        { signal }
      );
    },
    getNextPageParam: (lastMyOffers) => lastMyOffers.after,
    flattenKey: 'offers',
    enabled: !loadingLoggedInUser && isLoggedIn,
    staleTime: 0,
    cacheTime: 0,
  });

  const onLoadMoreMyOffersClick = () => {
    sendEvent({
      action: 'paginate',
      category: gaEventCategory,
    });

    fetchNextPage();
  };

  useIntersectionObserver({
    enabled: isScrollPaginationEnabled && hasNextPage && !isFetching,
    target: fetchNextPageRef,
    onIntersect: fetchNextPage,
    rootMargin: '500px',
    threshold: 0,
  });

  if (isAuthLoading || isInitialLoading) {
    return <Loading />;
  }

  if (isSuccess && offers.length === 0) {
    return <EmptyMyOffersList />;
  }

  return (
    <>
      <Box className="px-4 pt-2">
        {isSuccess &&
          offers.map((offer) => (
            <OfferSearchListCard
              key={offer.key}
              className="px-0 py-6"
              disabled={
                !(
                  isValidApprovedOffer(offer.offerStatusName) ||
                  isOfferFinished(offer.offerStatusName) ||
                  OFFER.STATUSES.RECENTS === offer.offerStatusName
                )
              }
              gaEventCategory={gaEventCategory}
              selectedOfferStats={{
                offerComments: true,
                offerClicks: true,
                reactions: true,
              }}
              showOfferPrice={false}
              showPublishDetails={false}
              showOfferStatusBadge
              {...offer}
            />
          ))}
        <div ref={fetchNextPageRef} />
      </Box>
      <ListLoadMoreButton
        hasMore={hasNextPage}
        hasNextPage={hasNextPage}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isAuthLoading || isLoading}
        isScrollPaginationEnabled={isScrollPaginationEnabled}
        onLoadMore={onLoadMoreMyOffersClick}
      />
    </>
  );
};

MyOffersList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  isAuthLoading: PropTypes.bool.isRequired,
};

export default MyOffersList;
